/**
 * Vulgar-fraction percentages — "66 2/3%", "33 1/3%" — as a margin grid prints
 * them, and as OCR mangles them.
 *
 * The shapes live here because two callers need the same answer: numericOf()
 * parses the value, and validateZeroAmount() decides whether the spelled/digit
 * cross-check has anything left to rescue. They were written separately, the
 * parser learned the slash-dropped and slash-as-space shapes, the guard did
 * not, and rows whose value was already 33.33% were stamped REEXTRACTED from a
 * neighbouring tier's phrase.
 */

/** The whole/numerator/denominator shapes a margin grid reaches us in. */
const VULGAR_FRACTION_SHAPES = [
  /(\d{1,3})\s+(\d+)\s*\/\s*(\d+)\s*%/,        // 66 2/3%
  /\(\s*(\d{2,3})\s+(\d)\s+(\d)\s*%\s*\)/,     // (66 2 3%) — slash as space
  /\(\s*(\d{2,3})\s+(\d)(\d)\s*%\s*\)/,        // (66 23%)  — slash dropped
  /(\d{1,3})\s*-\s*(\d+)\s*\/\s*(\d+)\s*%/,     // 66-2/3%   — hyphen separator
];

/**
 * The percentage a vulgar-fraction string denotes, as a decimal, or null when
 * the string is not one. `num < den` keeps "(80 33%)" from reading as a
 * fraction.
 */
export function parseVulgarFractionPercent(raw: string | null | undefined): number | null {
  const text = raw || '';
  for (const shape of VULGAR_FRACTION_SHAPES) {
    const match = text.match(shape);
    if (!match) continue;
    const whole = parseInt(match[1], 10);
    const numerator = parseInt(match[2], 10);
    const denominator = parseInt(match[3], 10);
    if (denominator > 0 && numerator < denominator) {
      return (whole + numerator / denominator) / 100;
    }
  }
  return null;
}

/** Whether a value string is a vulgar fraction this module can read. */
export function isVulgarFractionPercent(raw: string | null | undefined): boolean {
  return parseVulgarFractionPercent(raw) != null;
}

const THIRD = 1 / 3;
const TWO_THIRDS = 2 / 3;
const THIRD_TOLERANCE = 0.0005;

/**
 * Whether a parsed decimal already sits on a clean third or two-thirds.
 *
 * A margin grid states its tiers as thirds, and an OCR shape this module does
 * not yet read can still parse to the right number by another route. Such a
 * row has nothing for the cross-check to rescue, so it is held back from it on
 * the value as well as on the string. None of the corruptions the cross-check
 * exists for — "(2 0.0%)" reading 2%, "(1 10 %)" reading 1% — lands on a third.
 */
export function isCleanThirdPercent(decimal: number | null | undefined): boolean {
  if (decimal == null || !Number.isFinite(decimal)) return false;
  const asPercent = decimal * 100;
  return Math.abs(asPercent / 100 - THIRD) < THIRD_TOLERANCE
    || Math.abs(asPercent / 100 - TWO_THIRDS) < THIRD_TOLERANCE;
}

/**
 * The fractional tail of a spelled percentage — "and two-thirds", "and
 * one-half" — as a decimal share of one percentage point.
 *
 * Plurals and the joined spellings are here because the source documents use
 * them: "sixty-six and two-thirds percent" appears four times in the MUFG
 * grid, "onethird" and "sixtysix" once each where the PDF dropped the hyphen.
 * The earlier table held singular forms only, so "two-thirds" resolved to
 * nothing and the recovery walked on to the next tier in the same sentence —
 * which is how a 66 2/3 row came out holding 33.33%.
 */
const SPELLED_FRACTIONS: Record<string, number> = {
  half: 1 / 2,
  quarter: 1 / 4,
  quarters: 1 / 4,
  third: 1 / 3,
  thirds: 1 / 3,
};

/** "two-thirds" / "three quarters" → the fraction, or null. */
export function spelledFractionValue(phrase: string | null | undefined): number | null {
  const norm = (phrase || '').toLowerCase().replace(/[-\s]+/g, ' ').trim();
  if (!norm) return null;

  const direct = SPELLED_FRACTIONS[norm];
  if (direct != null) return direct;

  const COUNTS: Record<string, number> = { one: 1, a: 1, two: 2, three: 3, four: 4 };
  const parts = norm.match(/^(\w+)\s+(\w+)$/);
  if (parts) {
    const count = COUNTS[parts[1]];
    const unit = SPELLED_FRACTIONS[parts[2]];
    if (count != null && unit != null && count * unit < 1) return count * unit;
  }

  // "onethird" / "twothirds" — the PDF dropped the separator.
  for (const [word, unit] of Object.entries(SPELLED_FRACTIONS)) {
    if (!norm.endsWith(word)) continue;
    const lead = norm.slice(0, -word.length);
    const count = COUNTS[lead];
    if (count != null && count * unit < 1) return count * unit;
  }
  return null;
}
